import React, { useCallback, useState } from 'react';

import {
  Alert,
  Box,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import {
  CustomButton,
  Flex,
  ResponsiveBreak,
  SelectableWrapper,
} from 'src/components';

import {
  PositionSubscriptionCandidateUnsubscribeReasons,
  PositionSubscriptionReferrerUnsubscribeReasons,
  PositionSubscriptionType,
} from 'src/constants';

export const UnsubscribeReasonsForm: React.FC<{
  userName: string;
  type: PositionSubscriptionType;
  isLoading: boolean;
  isSubmitted: boolean;
  onSubmit: (reason: string) => void;
}> = ({ userName, type, isLoading, isSubmitted, onSubmit }) => {
  const [optionSelected, setOptionSelected] = useState<null | string>(null);
  const [reason, setReason] = useState<null | string>(null);

  const isSomethingElseOptionSelected = optionSelected === 'somethingElse';

  const handleSelection = (optionSelected: string) => {
    setOptionSelected(optionSelected);
    setReason(optionSelected === 'somethingElse' ? '' : optionSelected);

    if (optionSelected !== 'somethingElse') {
      onSubmit(optionSelected);
    }
  };

  const onProvideUnsubscribeReason = useCallback(() => {
    if (reason) {
      onSubmit(reason);
    }
  }, [reason]);

  const onKeyUp = (e: React.KeyboardEvent<any>) => {
    e.stopPropagation();
    if (e.code === 'Enter') {
      onProvideUnsubscribeReason();
    }
  };

  const firstName = userName.split(' ')[0];

  const unsubscribeReasons =
    type === PositionSubscriptionType.Candidate
      ? PositionSubscriptionCandidateUnsubscribeReasons
      : type === PositionSubscriptionType.Referral
      ? PositionSubscriptionReferrerUnsubscribeReasons
      : [];

  if (isSubmitted) {
    return (
      <>
        <Typography textAlign="left" variant="h1" mb={1}>
          You’ve successfully
          <br />
          unsubscribed
        </Typography>
        <Alert
          icon={false}
          severity="warning"
          sx={{ backgroundColor: '#fff4cc', marginBottom: 4 }}
        >
          <Typography color="text.primary" textAlign="left">
            You will not receive any more emails with our positions digest.
          </Typography>
        </Alert>
      </>
    );
  }

  return (
    <>
      <Typography variant="h4" fontWeight="600" mb={1}>
        Sorry to hear you’re
        <ResponsiveBreak breakpoint={'md'} /> no longer interested, {firstName}
      </Typography>

      <Typography variant="body1" mb={1}>
        You will not receive any more emails with our positions digest, but
        we’re still open to staying in touch.
      </Typography>

      <Box mb={1}>
        <Typography
          variant="body2"
          color="text.secondary"
          mb={{ xs: 4, md: 2 }}
          ml={{ md: 3 }}
        >
          Please share why you are not interested:
        </Typography>{' '}
        {unsubscribeReasons.map((option) => (
          <Box key={option} m={{ xs: '0 0 0.25rem 0', lg: '0 1rem 0.25rem 0' }}>
            <SelectableWrapper
              value={option}
              onClick={() => handleSelection(option)}
              isSelected={optionSelected === option}
            >
              <FormControlLabel
                value={option}
                control={<Box width={10} />}
                label={<Typography variant="body1">{option}</Typography>}
              />
            </SelectableWrapper>
          </Box>
        ))}
        <Box m={{ xs: '0 0 0.5rem 0', lg: '0 1rem 0.25rem 0' }}>
          {!isSomethingElseOptionSelected ? (
            <SelectableWrapper
              value="Something else"
              onClick={() => handleSelection('somethingElse')}
            >
              <FormControlLabel
                value="Something else"
                control={<Box width={10} />}
                label={
                  <>
                    <Typography variant="body1">Something else...</Typography>

                    <Typography variant="body2" color="text.secondary">
                      Click to add your own reason, this will{' '}
                      <ResponsiveBreak breakpoint="sm" />
                      greatly help us to improve our service
                    </Typography>
                  </>
                }
              />
            </SelectableWrapper>
          ) : (
            <>
              <Box
                borderRadius={1}
                bgcolor="highlight.accent"
                p="0.8rem 1.3rem"
              >
                <TextField
                  name="otherUnsubscribeOption"
                  type="text"
                  label="Something else"
                  id="otherUnsubscribeOption"
                  variant="outlined"
                  fullWidth
                  autoFocus
                  sx={{ background: 'white' }}
                  onChange={(e) => setReason(e.target.value)}
                  onKeyUp={onKeyUp}
                  inputProps={{ maxLength: 255 }}
                />
              </Box>
              <Flex width={{ xs: '100%', sm: '10rem' }} mt={2}>
                <CustomButton
                  isLoading={isLoading}
                  isDisabled={!reason}
                  onClick={onProvideUnsubscribeReason}
                  label="Submit"
                  testid="submit-something-else"
                  fullWidth
                />
              </Flex>
            </>
          )}
        </Box>
      </Box>
    </>
  );
};
